import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { setAuth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return 'Falha na autenticação.';
};

export default function LoginPage() {
  const { t } = useLang();
  useSEO('Entrar | Eco Collection', 'Acesso à área reservada Eco Collection.');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim().toLowerCase(), password: form.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatDetail(data.detail));
      setAuth(data.token, data.user);
      toast.success(`${t('auth.welcome')} ${data.user.name || data.user.email}`);
      const next = params.get('next');
      const fallback = data.user.role === 'admin' ? '/admin' : '/partner';
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/login') ? next : fallback;
      navigate(safeNext, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="grid min-h-screen place-items-center bg-eco-deep px-5">
      <div className="w-full max-w-sm">
        <Link to="/" data-testid="login-back-link" className="eco-btn-text mb-8 !text-white/70 hover:!text-eco-gold"><ArrowLeft size={15} /> Eco Collection</Link>
        <form data-testid="login-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-white p-8 shadow-2xl">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-eco-green/10 text-eco-green"><Lock size={20} /></span>
          <h1 className="mt-4 text-2xl font-bold tracking-[-0.02em] text-eco-ink">{t('auth.title')}</h1>
          <p className="mt-1 text-sm text-eco-ink/55">{t('auth.sub')}</p>
          <label className="mt-6 block">
            <span className="eco-field-label mb-1.5 block">{t('form.email')}</span>
            <input required type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="eco-input" data-testid="login-email" />
          </label>
          <label className="mt-4 block">
            <span className="eco-field-label mb-1.5 block">{t('auth.password')}</span>
            <input required type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="eco-input" data-testid="login-password" />
          </label>
          {error && <p data-testid="login-error" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit" className="eco-btn-primary mt-6 min-h-12 w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : t('nav.login')}
          </button>
        </form>
      </div>
    </div>
  );
}
